import { useEffect, useState } from "react";

/**
 * Custom hook to get the current screen width
 * @returns The current window width in pixels, or undefined during SSR
 */
export function useScreenWidth() {
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    // Handler to update width
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    // Set initial width
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

/**
 * Custom hook to check if screen width is within a specific breakpoint
 * @param breakpoint - The breakpoint width in pixels
 * @returns Boolean indicating if the screen width is less than or equal to the breakpoint
 */
export function useIsScreenWidth(breakpoint: number) {
  const [isWithinBreakpoint, setIsWithinBreakpoint] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const handleChange = () => {
      setIsWithinBreakpoint(window.innerWidth <= breakpoint);
    };

    // Set initial value
    handleChange();

    // Add event listener
    mql.addEventListener("change", handleChange);

    // Remove event listener on cleanup
    return () => mql.removeEventListener("change", handleChange);
  }, [breakpoint]);

  return !!isWithinBreakpoint;
}
